import { createContext, useContext, useState, useCallback } from "react";
import { toast } from "sonner";

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const getAuthHeader = useCallback(() => {
    if (!credentials) return {};
    const encoded = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${encoded}` };
  }, [credentials]);

  const login = async (username, password) => {
    const encoded = btoa(`${username}:${password}`);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`,
        {
          headers: {
            Authorization: `Basic ${encoded}`,
          },
          cache: "no-store",
        }
      );

      if (response.ok) {
        setCredentials({ username, password });
        setIsAuthenticated(true);
        return true;
      }
      
      if (response.status === 429) {
        toast.error("Too many failed attempts. Please try again later.");
      } else {
        toast.error("Invalid credentials");
      }
      return false;
    } catch (error) {
      toast.error("Authentication failed");
      return false;
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setCredentials(null);
  };

  const checkSession = useCallback(async () => {
    if (!credentials?.username || !credentials?.password) {
      setIsAuthenticated(false);
      return false;
    }

    const encoded = btoa(`${credentials.username}:${credentials.password}`);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`,
        {
          headers: {
            Authorization: `Basic ${encoded}`,
          },
          cache: "no-store",
        }
      );

      if (response.ok) {
        setIsAuthenticated(true);
        return true;
      }
    } catch (error) {
      // Fall through to logout below when verification fails.
    }

    setIsAuthenticated(false);
    setCredentials(null);
    return false;
  }, [credentials]);

  const value = {
    isAuthenticated,
    login,
    logout,
    checkSession,
    getAuthHeader,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
